import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SocialLoginModal = ({ platform, onClose, onLogin }) => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const platformDetails = {
    facebook: { name: 'Facebook', icon: 'Facebook', color: '#1877F2' },
    x: { name: 'X', icon: 'Twitter', color: '#000000' },
    instagram: { name: 'Instagram', icon: 'Instagram', color: '#E4405F' },
    tiktok: { name: 'TikTok', icon: 'Music', color: '#000000' }
  };

  const currentPlatform = platformDetails?.[platform] || platformDetails?.facebook;

  const handleLogin = async () => {
    if (!credentials?.username || !credentials?.password) {
      setError('Please enter both username and password');
      return;
    }

    setIsLoading(true);
    setError('');

    // Simulate login process
    setTimeout(() => {
      setIsLoading(false);
      if (onLogin) {
        onLogin({
          platform,
          username: credentials?.username,
          timestamp: new Date()?.toISOString()
        });
      }
      onClose();
    }, 1000);
  };

  return (
    <div className="fixed inset-0 z-[250] bg-gradient-to-br from-purple-900/40 via-black/70 to-cyan-900/40 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border-2 border-purple-500/30 shadow-2xl shadow-purple-500/20 w-full max-w-md transform animate-in zoom-in-95 duration-300">
        {/* Header with gradient accent */}
        <div className="p-4 md:p-6 border-b border-purple-500/20 bg-gradient-to-r from-purple-600/10 to-cyan-600/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
                <Icon name={currentPlatform?.icon} size={20} color="#FFFFFF" />
              </div>
              <h2 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Login to {currentPlatform?.name}
              </h2>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              iconName="X"
              className="hover:bg-purple-500/20 text-slate-300 hover:text-white transition-colors"
            />
          </div>
        </div>

        <div className="p-4 md:p-6 space-y-4">
          {/* Info box with cyan accent */}
          <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/30 rounded-xl p-4 flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
              <Icon name="Info" size={18} color="#06B6D4" />
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">
              Login to your {currentPlatform?.name} account to share your photo ranking results.
            </p>
          </div>

          {/* Form inputs */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-purple-300 mb-2">
                Username or Email
              </label>
              <Input
                type="text"
                placeholder="Enter your username or email"
                value={credentials?.username}
                onChange={(e) => setCredentials({ ...credentials, username: e?.target?.value })}
                disabled={isLoading}
                className="bg-slate-800/50 border-purple-500/30 focus:border-cyan-500 focus:ring-cyan-500/30 text-white placeholder:text-slate-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-purple-300 mb-2">
                Password
              </label>
              <Input
                type="password"
                placeholder="Enter your password"
                value={credentials?.password}
                onChange={(e) => setCredentials({ ...credentials, password: e?.target?.value })}
                disabled={isLoading}
                className="bg-slate-800/50 border-purple-500/30 focus:border-cyan-500 focus:ring-cyan-500/30 text-white placeholder:text-slate-500"
              />
            </div>

            {/* Error message */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex items-start gap-3 animate-in slide-in-from-top-2 duration-200">
                <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={18} color="#EF4444" />
                </div>
                <p className="text-sm text-red-300 leading-relaxed">{error}</p>
              </div>
            )}
          </div>

          {/* Security notice with emerald accent */}
          <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-xl p-3 flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <Icon name="Lock" size={18} color="#10B981" />
            </div>
            <p className="text-xs text-emerald-300 leading-relaxed">
              Your credentials are encrypted and never stored. We only use them to post on your behalf.
            </p>
          </div>
        </div>

        {/* Footer with action buttons */}
        <div className="p-4 md:p-6 border-t border-purple-500/20 bg-gradient-to-r from-purple-600/5 to-cyan-600/5 flex items-center gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            fullWidth
            disabled={isLoading}
            className="border-purple-500/30 text-purple-300 hover:bg-purple-500/10 hover:border-purple-400/50 transition-all"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleLogin}
            disabled={isLoading}
            iconName={isLoading ? 'Loader2' : 'LogIn'}
            iconPosition="left"
            fullWidth
            className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 text-white font-semibold shadow-lg shadow-purple-500/30 transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Logging in...' : 'Login & Share'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SocialLoginModal;